import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Copy, Gift, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PromoCode = () => {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [myCodes, setMyCodes] = useState([]);
  const [redeemCode, setRedeemCode] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadMyCodes();
  }, []);

  const loadMyCodes = async () => {
    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/promo/my-codes`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMyCodes(data.codes || []);
      }
    } catch (error) {
      console.error('Error loading codes:', error);
    }
  };

  const generateCode = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/promo/generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            description: 'Accès gratuit Spirify - Partagé avec ami',
            max_uses: null  // Illimité
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPromoCode(data.code);
        setMessage('Code généré avec succès ! Partagez-le avec vos amis.');
        loadMyCodes();
      } else {
        setMessage('Erreur lors de la génération du code.');
      }
    } catch (error) {
      console.error('Error generating code:', error);
      setMessage('Erreur de connexion.');
    } finally {
      setIsLoading(false);
    }
  };

  const redeemPromoCode = async () => {
    if (!redeemCode.trim()) {
      setMessage('Entrez un code promo.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/promo/redeem`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code: redeemCode.toUpperCase().trim() })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ ' + data.message);
        setRedeemCode('');
        setTimeout(() => {
          window.location.reload();  // Recharger pour mettre à jour le statut
        }, 2000);
      } else {
        setMessage('❌ ' + (data.detail || 'Code invalide'));
      }
    } catch (error) {
      console.error('Error redeeming code:', error);
      setMessage('❌ Erreur de connexion.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('✅ Code copié dans le presse-papier !');
    setTimeout(() => setMessage(''), 3000);
  };

  const shareCode = (code) => {
    const shareText = `Rejoins-moi sur Spirify ! Utilise mon code promo: ${code}\n\nDécouvre l'islam, le christianisme, le judaïsme et la spiritualité en 5 minutes par jour 🌟`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Code Promo Spirify',
        text: shareText
      });
    } else {
      copyToClipboard(shareText);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🎁 Codes Promo Spirify
          </h1>
          <p className="text-gray-600">
            Partagez Spirify avec vos amis - accès gratuit pour tous !
          </p>
        </div>

        {/* Message */}
        {message && (
          <Card className="mb-6 border-teal-200 bg-teal-50">
            <CardContent className="p-4">
              <p className="text-sm text-center">{message}</p>
            </CardContent>
          </Card>
        )}

        {/* Générer un code */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <Gift className="w-6 h-6 mr-2 text-teal-600" />
              Générer un Code Promo
            </h2>
            <p className="text-gray-600 mb-4">
              Créez un code pour donner un accès gratuit à vos amis
            </p>

            <Button
              onClick={generateCode}
              disabled={isLoading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white mb-4"
            >
              {isLoading ? 'Génération...' : 'Générer un nouveau code'}
            </Button>

            {promoCode && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Votre nouveau code :</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-4 py-3 rounded text-2xl font-bold text-center text-purple-600">
                    {promoCode}
                  </code>
                  <Button
                    onClick={() => copyToClipboard(promoCode)}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => shareCode(promoCode)}
                    variant="outline"
                    size="sm"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Utiliser un code */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
              Utiliser un Code Promo
            </h2>
            <p className="text-gray-600 mb-4">
              Vous avez reçu un code ? Activez-le ici
            </p>

            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Entrez votre code (ex: ABC123XY)"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                className="flex-1"
                maxLength={10}
              />
              <Button
                onClick={redeemPromoCode}
                disabled={isLoading || !redeemCode.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                Activer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mes codes générés */}
        {myCodes.length > 0 && (
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Mes Codes Générés</h2>
              <div className="space-y-3">
                {myCodes.map((code, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <code className="text-lg font-bold text-purple-600">
                        {code.code}
                      </code>
                      <p className="text-sm text-gray-600 mt-1">
                        {code.description}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline">
                          {code.uses_count} utilisation(s)
                        </Badge>
                        {code.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Actif</Badge>
                        ) : (
                          <Badge variant="destructive">Inactif</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => copyToClipboard(code.code)}
                        variant="outline"
                        size="sm"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => shareCode(code.code)}
                        variant="outline"
                        size="sm"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Retour au dashboard */}
        <div className="mt-6 text-center">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
          >
            Retour au Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PromoCode;
